import React, { useState, useEffect, useRef, useCallback } from "react";
import hideIcon from "../assets/svg/eye_open.svg";
import showIcon from "../assets/svg/eye_closed.svg";
import { CharacterCounter } from "./CharacterCounter";
import emailIcon from "../assets/svg/registrationIcons/email-icon.svg";
import nameIcon from "../assets/svg/registrationIcons/name-icon.svg";

interface InputFieldRegister {
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  name?: string;
  ref?: React.RefCallback<HTMLInputElement | HTMLTextAreaElement>;
}

interface InputFieldProps {
  id: string;
  value?: string;
  label?: any;
  labelColor?: string;
  icon?: string;
  inputType?: "text" | "password" | "amount" | "tip" | "email" | "number";
  placeholder?: string;
  register?: InputFieldRegister;
  requiredFlag?: boolean;
  password?: boolean;
  defaultValue?: string;
  maxLength?: number;
  noResize?: boolean;
  readOnly?: boolean;
  name?: string;
  autoComplete?: string;
  onInputChange?: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  multiLine?: boolean;
  multiLineMinHeight?: number;
  currentValueLength?: number;
  dark?: boolean;
  totalAmount?: number;
  anonymousMode?: boolean;
}

export const InputField: React.FC<InputFieldProps> = ({
  id,
  value,
  label,
  labelColor,
  icon: Icon,
  inputType = "text",
  placeholder,
  register = {},
  requiredFlag = false,
  password = false,
  defaultValue = "",
  maxLength = 500,
  noResize,
  readOnly = false,
  name,
  autoComplete,
  onInputChange,
  multiLine,
  multiLineMinHeight = 100,
  currentValueLength,
  dark,
  totalAmount = 0,
  anonymousMode,
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(
    value || defaultValue || ""
  );
  const [tipPercentage, setTipPercentage] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);

  const {
    onChange: hookFormOnChange,
    onBlur: hookFormOnBlur,
    name: hookFormName,
    ref: hookFormRef,
  } = register;

  // Synchronize internal state when the value or defaultValue prop changes.
  useEffect(() => {
    setInputValue(value || defaultValue || "");
  }, [value, defaultValue]);

  // Use a mutable ref for the input element.
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(
    null
  ) as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;

  // Merge local ref with external ref only if it's a function.
  const mergedRef = useCallback(
    (el: HTMLInputElement | HTMLTextAreaElement | null) => {
      inputRef.current = el;
      if (typeof hookFormRef === "function") {
        hookFormRef(el);
      }
    },
    [hookFormRef]
  );

  const finalInputType = password
    ? showPassword
      ? "text"
      : "password"
    : inputType;
  const finalName = hookFormName || name || id;
  const finalAutoComplete =
    autoComplete || (password ? "current-password" : "on");

  // Update tip percentage dynamically when input changes.
  useEffect(() => {
    if (inputType === "tip" && totalAmount > 0) {
      const tipAmount = parseFloat(inputValue) || 0;
      const percentage = ((tipAmount / totalAmount) * 100).toFixed(2);
      setTipPercentage(`${percentage}%`);
    }
  }, [inputValue, totalAmount, inputType]);

  // Check for auto-filled value after component mounts or when auto-fill occurs.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current && inputRef.current.value !== inputValue) {
        setInputValue(inputRef.current.value);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [inputValue]);

  return (
    <div
      className={`${
        dark
          ? readOnly
            ? "bg-[#1b1e2c]"
            : "bg-[#1b1e2c] focus-within:ring-blue-500 focus-within:ring-1"
          : readOnly
          ? "bg-gray-800"
          : "bg-gray-800 focus-within:bg-gray-700"
      } w-full rounded-2xl px-2 hover:bg-opacity-80 transition ease-in-out`}
    >
      <div className="flex justify-between items-center p-2">
        <div className="w-full">
          <label htmlFor={id} className="block text-xs mb-1 text-left">
            <span
              className={`${
                labelColor
                  ? labelColor
                  : dark
                  ? "text-blue-500"
                  : "text-slate-100"
              }`}
            >
              {label}{" "}
            </span>{" "}
            {requiredFlag && <span className="text-red-500">*</span>}
          </label>
          <div className="flex items-center w-full relative">
            {multiLine ? (
              <>
                <textarea
                  className={`${
                    noResize ? "resize-none" : ""
                  } flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-sm w-full pr-10`}
                  maxLength={maxLength}
                  readOnly={readOnly}
                  defaultValue={!!defaultValue ? defaultValue : ""}
                  {...register}
                  style={
                    !!multiLineMinHeight
                      ? { minHeight: multiLineMinHeight + "px" }
                      : {}
                  }
                  id={id}
                  placeholder={placeholder}
                />
              </>
            ) : (
              <input
                style={
                  {
                    WebkitTextSecurity: anonymousMode ? "disc" : "unset",
                  } as any
                }
                id={id}
                value={inputValue}
                name={finalName}
                type={finalInputType}
                placeholder={placeholder}
                autoComplete={finalAutoComplete}
                maxLength={maxLength}
                readOnly={readOnly}
                onFocus={() => {
                  if (inputType === "tip") {
                    setIsEditing(true);
                  }
                }}
                onBlur={(e) => {
                  if (inputType === "tip") {
                    setIsEditing(false);
                    const formattedValue = parseFloat(
                      e.target.value || "0"
                    ).toFixed(2);
                    setInputValue(formattedValue);
                  }
                  hookFormOnBlur?.(e);
                }}
                onChange={(e) => {
                  let newValue = e.target.value;

                  // Apply special logic only if input type is "tip"
                  if (inputType === "tip") {
                    // Remove non-numeric characters except dot
                    newValue = newValue.replace(/[^0-9.]/g, "");

                    // Prevent multiple dots
                    const dotCount = (newValue.match(/\./g) || []).length;
                    if (dotCount > 1) {
                      newValue = newValue.slice(0, newValue.lastIndexOf("."));
                    }

                    // Auto-format only if not actively typing (avoid flicker)
                    if (!isEditing) {
                      newValue = parseFloat(newValue || "0").toFixed(2);
                    }
                  }

                  setInputValue(newValue);
                  hookFormOnChange?.(e);
                  onInputChange?.(e);
                }}
                ref={mergedRef}
                className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none text-sm w-full pr-10"
              />
            )}
            {/* Tip Percentage Display (Only for "tip" input type) */}
            {inputType === "tip" && totalAmount > 0 && (
              <span className="absolute right-2 text-gray-300 text-xs">
                {tipPercentage}
              </span>
            )}
          </div>
        </div>
        {id === "email" || id === "usernameOrEmail" ? (
          <img src={emailIcon} alt="Email Icon" className="h-3 lg:h-4 ml-2" />
        ) : id === "firstName" || id === "lastName" || id === "username" ? (
          <img
            src={nameIcon}
            alt="User Info Icon"
            className="h-3 lg:h-4 ml-2"
          />
        ) : (
          Icon && <img src={Icon} alt="Icon" className="h-3 lg:h-4 ml-2" />
        )}
        {password && (
          <div className="flex justify-center items-center">
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              <img
                className="h-4 lg:h-5 cursor-pointer"
                role="button"
                src={showPassword ? hideIcon : showIcon}
                alt="Toggle password visibility"
              />
            </button>
          </div>
        )}
      </div>
      {currentValueLength !== undefined && !!maxLength && (
        <div className="flex justify-end">
          <CharacterCounter
            currentLength={currentValueLength}
            maxCharacterLimit={maxLength}
          />
        </div>
      )}
    </div>
  );
};
